'use client';

import type { BoxProps } from '@mui/material/Box';

import { useTranslations } from 'next-intl';

import { alpha } from '@mui/material/styles';
import { Box, Stack, Button, Container, Typography } from '@mui/material';

import { paths } from 'src/routes/paths';

import { Iconify } from 'src/components/iconify';

export default function HomeHero({ sx, ...other }: BoxProps) {
  const t = useTranslations('Home.hero');

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        minHeight: { xs: '88vh', md: '100vh' },
        display: 'flex',
        alignItems: 'flex-end',
        overflow: 'hidden',
        bgcolor: 'grey.900',
        color: 'common.white',
        ...sx,
      }}
      {...other}
    >
      {/* Background gradients */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          background: (theme) => `
            radial-gradient(ellipse 60% 60% at 70% 40%, ${alpha(theme.palette.primary.main, 0.35)} 0%, transparent 70%),
            radial-gradient(ellipse 40% 50% at 20% 80%, ${alpha(theme.palette.primary.main, 0.15)} 0%, transparent 60%),
            linear-gradient(160deg, #0d1520 0%, #141a21 50%, #1c252e 100%)
          `,
        }}
      />

      {/* Grid overlay */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage: (theme) => `
            linear-gradient(${alpha(theme.palette.primary.main, 0.08)} 1px, transparent 1px),
            linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.08)} 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 30%, transparent 80%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 80% 80% at 50% 50%, black 30%, transparent 80%)',
        }}
      />

      {/* Right glow */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: '15%',
          right: '8%',
          width: 480,
          height: 480,
          borderRadius: '50%',
          background: (theme) =>
            `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.22)} 0%, transparent 70%)`,
          pointerEvents: 'none',
          display: { xs: 'none', md: 'block' },
        }}
      />

      <Container
        sx={{
          position: 'relative',
          zIndex: 2,
          px: { xs: 3, md: 4 },
          pb: { xs: 8, md: 22 },
          pt: { xs: 14, md: 0 },
        }}
      >
        <Box sx={{ maxWidth: 720 }}>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{
              mb: 2.5,
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: 1.5,
              textTransform: 'uppercase',
              color: 'primary.light',
            }}
          >
            <Box sx={{ width: 24, height: 2, bgcolor: 'primary.light', borderRadius: 1 }} />
            <Box component="span">{t('overline')}</Box>
          </Stack>

          <Typography
            component="h1"
            sx={{
              fontFamily: 'Barlow, sans-serif',
              fontWeight: 800,
              fontSize: { xs: 'clamp(40px, 11vw, 64px)', md: 'clamp(48px, 7vw, 88px)' },
              lineHeight: 1,
              letterSpacing: { xs: '-1.5px', md: '-2px' },
              textWrap: 'balance',
              mb: 3.5,
            }}
          >
            {t('title')}
            <br />
            <Box component="span" sx={{ color: 'primary.light' }}>
              {t('subtitle')}
            </Box>
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: 16, md: 18 },
              lineHeight: 1.65,
              color: alpha('#ffffff', 0.55),
              maxWidth: 520,
              mb: { xs: 4.5, md: 5.5 },
              textWrap: 'pretty',
            }}
          >
            {t('description')}
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.75}
            flexWrap="wrap"
            useFlexGap
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{
              '& > a': { width: { xs: '100%', sm: 'auto' }, justifyContent: 'center' },
            }}
          >
            <Button
              href={paths.contact}
              variant="contained"
              color="secondary"
              size="large"
              endIcon={<Iconify icon="ic:round-arrow-right-alt" />}
              sx={{
                px: 4,
                py: 1.75,
                borderRadius: 1.25,
                fontSize: 15,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                boxShadow: (theme) => `0 4px 24px ${alpha(theme.palette.secondary.main, 0.35)}`,
                '&:hover': {
                  boxShadow: (theme) => `0 8px 32px ${alpha(theme.palette.secondary.main, 0.45)}`,
                },
              }}
            >
              {t('contactUs')}
            </Button>
            <Button
              href={paths.auth.login}
              size="large"
              sx={{
                px: 3.5,
                py: 1.75,
                borderRadius: 1.25,
                fontSize: 15,
                fontWeight: 500,
                color: alpha('#ffffff', 0.72),
                bgcolor: alpha('#ffffff', 0.06),
                border: `1px solid ${alpha('#ffffff', 0.12)}`,
                '&:hover': { bgcolor: alpha('#ffffff', 0.1), color: '#fff' },
              }}
            >
              {t('login')}
            </Button>
          </Stack>
        </Box>
      </Container>

      {/* Floating stats */}
      <Stack
        spacing={1.5}
        sx={{
          position: 'absolute',
          bottom: 80,
          right: 32,
          zIndex: 3,
          display: { xs: 'none', md: 'flex' },
        }}
      >
        {[
          { num: '100+', label: t('stats.activeTeams') },
          { num: '50k+', label: t('stats.testResults') },
        ].map((stat) => (
          <Box
            key={stat.label}
            sx={{
              minWidth: 160,
              px: 2.5,
              py: 1.75,
              borderRadius: 1.5,
              bgcolor: alpha('#ffffff', 0.05),
              border: `1px solid ${alpha('#ffffff', 0.08)}`,
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Barlow, sans-serif',
                fontWeight: 800,
                fontSize: 28,
                lineHeight: 1,
                color: 'common.white',
              }}
            >
              {stat.num}
            </Typography>
            <Typography sx={{ fontSize: 12, color: alpha('#ffffff', 0.45), mt: 0.5 }}>
              {stat.label}
            </Typography>
          </Box>
        ))}
      </Stack>

      {/* Scroll cue */}
      <Stack
        alignItems="center"
        spacing={1}
        sx={{
          position: 'absolute',
          bottom: 36,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 3,
          color: alpha('#ffffff', 0.3),
          fontSize: 11,
          letterSpacing: 1,
          textTransform: 'uppercase',
          display: { xs: 'none', md: 'flex' },
        }}
      >
        <Box
          sx={{
            width: '1px',
            height: 48,
            background: `linear-gradient(to bottom, ${alpha('#ffffff', 0.3)}, transparent)`,
          }}
        />
        {t('scroll')}
      </Stack>
    </Box>
  );
}
